import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import * as moment from 'moment';
import * as URI from 'urijs';

import { AppConfig } from '®shared/config';
import {
  ContainerModes,
  PublishListAction,
  PublishListAndRequestReviewAction,
} from '®shared/container-structure';
import { CurrentUserAndPermissions } from '®shared/current-user';
import { Loadable, Saveable } from '®shared/data';
import { HierarchyNode } from '®shared/hierarchy';
import {
  Permissions,
  UserPermissions,
  hasAnyListEditPermission,
  hasPermissionForListId,
} from '®shared/permissions';
import { ALL_DEFAULT_CITATION_STYLES } from '®shared/reading-list/citation-switcher';
import {
  ArchiveListShowModalAction,
  DeleteListShowModalAction,
} from '®shared/reading-list/edit-modals';
import { NodesEditorShowModalAction } from '®shared/reading-list/hierarchy/editor';
import { ListDetailsEditorShowModalAction } from '®shared/reading-list/list-details/editor';
import { ListOwnersEditorShowModalAction } from '®shared/reading-list/list-owners';
import {
  EditableList,
  ListExportUrls,
  ListPublishingState,
  ReadingListDetails,
} from '®shared/reading-list/models';
import { RequestReviewShowModalAction } from '®shared/reviews/request-review';
import { ReviewHistoryShowModalAction } from '®shared/reviews/review-history';
import { ListAccess } from '®shared/tenant';
import { UriService } from '®shared/url-utils';
import { UserRecord } from '®shared/user';

import { getLabelClassForPeriod } from '../../tenant/period-label';
import {
  showPublishButton,
  showRequestReviewButton,
  showReviewAndPublishButton,
} from '../action-buttons';
import { BasicCitationStyle } from '../citation-switcher/basic-citation-style';

/**
 * Component for presenting list-details
 */
@Component({
  selector: 'rl-list-details',
  templateUrl: './list-details.component.html',
})
export class ListDetailsComponent implements OnChanges, OnInit {
  ContainerModes = ContainerModes;

  @Input() list: ReadingListDetails;
  @Input() publishedStatusSaveable: Saveable;
  @Input() isInMyLists: Loadable<boolean> | null;
  @Input() defaultListAccess: ListAccess | null;
  @Input() containerMode: ContainerModes;
  @Input() currentCitationStyle: BasicCitationStyle | null;
  @Input() currentUser: CurrentUserAndPermissions;
  @Input() tenantCitationStyles: BasicCitationStyle[];
  @Input() editableList: EditableList;
  @Input() listPublishingStatus: ListPublishingState;
  @Input() queueHasPublishAction?: boolean;
  @Input() queueSize: number;

  @Output() addToMyLists: EventEmitter<string> = new EventEmitter();
  @Output() showDeleteListModal = new EventEmitter<DeleteListShowModalAction>();
  @Output() showArchiveListModal =
    new EventEmitter<ArchiveListShowModalAction>();
  @Output() showEditListDetailsModal =
    new EventEmitter<ListDetailsEditorShowModalAction>();
  @Output() showAssignListOwnersModal =
    new EventEmitter<ListOwnersEditorShowModalAction>();
  @Output()
  showRequestReviewModal = new EventEmitter<RequestReviewShowModalAction>();
  @Output()
  showReviewHistoryModal = new EventEmitter<ReviewHistoryShowModalAction>();
  @Output()
  showStudentNumbersAndHierarchyModal =
    new EventEmitter<NodesEditorShowModalAction>();
  @Output() publishList: EventEmitter<PublishListAction> = new EventEmitter();
  @Output() noChangesToPublish = new EventEmitter();
  @Output()
  showRequestReviewModalAndPublishList: EventEmitter<PublishListAndRequestReviewAction> =
    new EventEmitter();
  @Output() updateUserRecord = new EventEmitter<UserRecord>();

  baseUri = this.appConfig.tenant.httpsBaseUri;
  dashboardUrl: string;
  exportUrls?: ListExportUrls = undefined;
  nodesWithIds: HierarchyNode[];
  recentChangesUrl: string;
  tarlListViewUrl: string;
  v2DashboardEnabled = this.appConfig.tenant.v2DashboardEnabled;
  reviewWorkflow = this.appConfig.tenant.featureFlags.reviewWorkflow;
  showBookmarkingExtensionAlert = true;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private appConfig: AppConfig,
    private uriService: UriService,
  ) {}

  get permissions(): UserPermissions {
    return this.currentUser.permissions;
  }

  get isListAdmin(): boolean {
    const dashboardVisibilityPermissions = [
      Permissions.EDIT_LIST,
      Permissions.PUBLISH_LIST,
    ];

    return dashboardVisibilityPermissions.some(permission =>
      hasPermissionForListId(
        permission,
        this.editableList.id,
        this.permissions,
      ),
    );
  }

  get isListOwner(): boolean {
    return (
      this.editableList.owners &&
      this.editableList.owners.some(
        owner => owner.id && owner.id === this.currentUser.oauthUser.guid,
      )
    );
  }

  get canViewEditMenu(): boolean {
    return hasAnyListEditPermission(
      this.list,
      this.editableList,
      this.permissions,
    );
  }

  get canViewStudentNumbers(): boolean {
    const studentNumberVisibilityPermissions = [
      Permissions.EDIT_LIST,
      Permissions.VIEW_ACQUISITIONS,
      Permissions.REQUEST_REVIEW,
    ];

    return studentNumberVisibilityPermissions.some(permission =>
      hasPermissionForListId(
        permission,
        this.editableList.id,
        this.permissions,
      ),
    );
  }

  get canViewInternalNote(): boolean {
    return this.containerMode === ContainerModes.Edit;
  }

  get isEditMode(): boolean {
    return this.containerMode === ContainerModes.Edit;
  }

  ngOnInit() {
    this.tarlListViewUrl = this.uriService.getTarlListViewUrl(
      this.list.id,
      this.list.is_draft,
    );

    this.recentChangesUrl = this.constructRecentChangesUrl(
      this.editableList.title,
    );
    this.dashboardUrl = this.constructV2DashboardUrl();

    const dateUserDismissedBookmarkExtensionAlert =
      this.currentUser.tarlUser?.date_bookmark_extension_alert_dismissed;

    if (dateUserDismissedBookmarkExtensionAlert) {
      this.showBookmarkingExtensionAlert = moment(
        dateUserDismissedBookmarkExtensionAlert,
      )
        .add(4, 'months')
        .isBefore(moment());
    } else {
      this.showBookmarkingExtensionAlert = true;
    }
  }

  ngOnChanges() {
    this.exportUrls = this.uriService.getExportUrls(
      this.list.id,
      this.getCitationStyleId(this.currentCitationStyle),
    );
    this.nodesWithIds = this.editableList ? this.editableList.nodes : [];
  }

  onPublishList() {
    if (!this.editableList.hasUnpublishedChanges) {
      this.noChangesToPublish.emit();
      return;
    }
    this.publishList.emit(
      new PublishListAction({ listId: this.editableList.id }),
    );
  }

  onRequestReview() {
    if (!this.editableList.hasUnpublishedChanges) {
      return;
    }
    this.showRequestReviewModal.emit(
      new RequestReviewShowModalAction({ listId: this.editableList.id }),
    );
  }

  onReviewAndPublish() {
    if (!this.editableList.hasUnpublishedChanges) {
      return;
    }
    this.showRequestReviewModalAndPublishList.emit(
      new PublishListAndRequestReviewAction({ listId: this.editableList.id }),
    );
  }

  get timePeriodLabelClass(): string {
    const { period } = this.editableList;
    return getLabelClassForPeriod(period);
  }

  get isPrivate(): boolean {
    const { listAccess = null } = this.editableList || {};
    if (listAccess === ListAccess.PRIVATE) {
      return true;
    } else if (listAccess === ListAccess.PUBLIC) {
      return false;
    }
    return this.defaultListAccess === ListAccess.PRIVATE;
  }

  get isEdit(): boolean {
    return this.containerMode === ContainerModes.Edit;
  }

  get showPublishButton(): boolean {
    return showPublishButton(
      this.containerMode,
      this.list,
      this.editableList,
      this.reviewWorkflow,
      this.queueHasPublishAction,
      this.currentUser.permissions,
    );
  }

  get showRequestReviewButton(): boolean {
    return showRequestReviewButton(
      this.containerMode,
      this.editableList,
      this.currentUser.permissions,
      this.reviewWorkflow,
    );
  }

  get showReviewAndPublishButton(): boolean {
    return showReviewAndPublishButton(
      this.containerMode,
      this.editableList,
      this.currentUser.permissions,
      this.reviewWorkflow,
    );
  }

  /**
   * Get the ID of the current citation style, replacing 'none' or 'default'
   * with the ID of the default style
   * @param {BasicCitationStyle|null} citationStyle - an optional citation style
   * @returns {string|null} - the citation style ID, if citation is available
   */
  getCitationStyleId(citationStyle: BasicCitationStyle | null): string | null {
    const tenantDefaultCitationStyle = this.getTenantDefaultCitationStyle();

    if (!citationStyle || !tenantDefaultCitationStyle) {
      return null;
    } else if (
      ALL_DEFAULT_CITATION_STYLES.find(style => citationStyle.id === style.id)
    ) {
      return tenantDefaultCitationStyle.id;
    }
    return citationStyle.id;
  }

  /**
   * Get the default tenant citation style
   * @returns {BasicCitationStyle|null} - the default citation style for the tenant, if one exists
   */
  getTenantDefaultCitationStyle(): BasicCitationStyle | null {
    return this.tenantCitationStyles.find(style => style.is_default);
  }

  getListOwnersLabel(owners: UserRecord[]): string {
    return owners.map(owner => owner.full_name).join(', ');
  }

  /**
   * Get the webpage URL for a given node.
   *
   * @param {Node} node - the node whose webpage we want
   * @returns {string} - the URL of the node's webpage
   **/
  getAbsoluteUrlToTARL(node: HierarchyNode): string {
    if (node.htmlPageUrl) {
      return new URI(this.baseUri).path(node.htmlPageUrl).toString();
    } else {
      return this.baseUri;
    }
  }

  /**
   * Builds the URL for the lists' Recent Changes page
   * @param  {string} title   List title used to populate TARL <h1>
   * @return {string} The URL to the equivalent Recent Changes page.
   */
  constructRecentChangesUrl(title: string): string {
    return new URI(this.baseUri)
      .segment(['lists', this.list.id, 'events.html'])
      .query({ title })
      .toString();
  }

  /**
   * Builds the URL for the lists' dashboard page
   * @return {string} The URL to the list's dashboard page.
   */
  constructV2DashboardUrl(): string {
    return new URI(this.baseUri)
      .segment(['lists', this.list.id, 'dashboard.html'])
      .toString();
  }

  dismissBookmarkingExtensionAlert() {
    const userRecord = UserRecord.create({
      id: this.currentUser.oauthUser.guid,
      date_bookmark_extension_alert_dismissed: moment().format(
        'YYYY-MM-DDTHH:mm:ssZ',
      ),
    });

    this.updateUserRecord.emit(userRecord);
    this.showBookmarkingExtensionAlert = false;
  }

  installBookmarkingExtension() {
    window.open(this.appConfig.bookmarkingExtensionUrl, '_blank');
  }
}
